import { useState, useEffect, useRef, useMemo, useCallback, type ReactNode } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Loader2, ChevronDown, AlertCircle, Package, FileText,
  Image, Truck, Search, Eye, AlertTriangle, Save, ArrowLeft,
  BoxSelect, Sparkles, Globe, Users, PackageOpen, Plus, Minus, Trash2,
} from 'lucide-react';
import { StickyBottomBar } from '@/components/ui/StickyBottomBar';
import { SaveButton } from '@/components/ui/SaveButton';
import { useProductPreviewImage } from '@/hooks/useProductPreviewImage';
import { useProductInterestCounts } from '@/hooks/useProductInterest';
import { useBundleItems, type BundleItem } from '@/hooks/useBundleItems';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import { ProductImage } from '@/components/admin/ProductImageUpload';
import { ProductImagesSection } from '@/components/product/ProductImagesSection';
import { RichTextEditor } from '@/components/common/RichTextEditor';
import { EditorLayout, EditorSidebarSection, EditorSidebarCard, EditorSectionCard, EditorSectionItem } from '@/components/editor';
import { ProductMiniPreview } from '@/components/studio';
import { ProducerProduct } from '@/hooks/useProducerProducts';
import { cn } from '@/lib/utils';

export type ProductStatusValue = 'draft' | 'public' | 'sold_out' | 'upcoming' | 'archived';

export type ProductTypeValue = 'simple' | 'bundle';

export interface BundleItemData {
  product_id: string;
  quantity: number;
  sort_order: number;
  /** Display-only, not sent to server */
  title?: string;
}

export interface ProductFormData {
  title: string;
  description: string;
  priceKr: string;
  status: ProductStatusValue;
  productType: ProductTypeValue;
  images: ProductImage[];
  seoTitle: string;
  seoDescription: string;
  seoImageUrl: string;
  pickupInstructions: string;
  stockLimit: number | null;
  bundleItems: BundleItemData[];
}

interface ProductFormProps {
  mode: 'create' | 'edit';
  producerId: string;
  productId?: string;
  product?: ProducerProduct | null;
  shopifyConfigured: boolean;
  syncing: string | null;
  isSaving: boolean;
  onSave: (data: ProductFormData, publish: boolean) => Promise<void>;
  onCancel: () => void;
  onSync?: () => Promise<void>;
  draftSessionId?: string;
  /** @deprecated Use draftSessionId instead. */
  ensureDraftExists?: () => Promise<string | null>;
  isCreatingDraft?: boolean;
  isPublishBlocked?: boolean;
  /** Danger zone content (delete), rendered as last accordion section in edit mode. */
  dangerZone?: ReactNode;
  /** Optional actions rendered in the header row (e.g. Share Studio button) */
  headerActions?: ReactNode;
}

// SectionSummary replaced by shared EditorSectionItem from @/components/editor

/* ── Status labels & helpers ── */
const STATUS_OPTIONS: { value: ProductStatusValue; label: string; hint: string }[] = [
  { value: 'draft', label: 'Utkast', hint: 'Dold – visas inte för kunder.' },
  { value: 'public', label: 'Publicerad', hint: 'Synlig i gårdsbutiken.' },
  { value: 'upcoming', label: 'Kommer snart', hint: 'Visas i gårdsbutiken men går inte att beställa ännu.' },
  { value: 'sold_out', label: 'Tillfälligt slut', hint: 'Visas i gårdsbutiken men går inte att beställa just nu.' },
];

function getStatusLabel(status: ProductStatusValue): string {
  return STATUS_OPTIONS.find(o => o.value === status)?.label ?? 'Utkast';
}

function getStatusHint(status: ProductStatusValue): string {
  return STATUS_OPTIONS.find(o => o.value === status)?.hint ?? '';
}

/* ── Quick-start chips for create mode (simple products only) ── */
const QUICK_START_CHIPS = [
  { label: 'Ägg', title: 'Ägg från gården' },
  { label: 'Honung', title: 'Honung från gården' },
  { label: 'Kött', title: 'Kött från gården' },
  { label: 'Grönsaker', title: 'Grönsaker från gården' },
  { label: 'Annat', title: '' },
];

/* ── Inline bundle product picker with search ── */
function BundleProductPicker({ producerId, excludeIds, onSelect, disabled }: {
  producerId: string;
  excludeIds: string[];
  onSelect: (product: { id: string; title: string }) => void;
  disabled?: boolean;
}) {
  const [allProducts, setAllProducts] = useState<{ id: string; title: string }[]>([]);
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const searchRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!open) return;
    (async () => {
      const { supabase } = await import('@/integrations/supabase/client');
      const { data } = await supabase
        .from('producer_products')
        .select('id, title')
        .eq('producer_id', producerId)
        .eq('product_type', 'simple')
        .is('deleted_at', null)
        .order('title');
      setAllProducts(data || []);
    })();
    setTimeout(() => searchRef.current?.focus(), 100);
  }, [open, producerId]);

  const filtered = useMemo(() => {
    const available = allProducts.filter(p => !excludeIds.includes(p.id));
    if (!search.trim()) return available;
    const q = search.toLowerCase();
    return available.filter(p => p.title.toLowerCase().includes(q));
  }, [allProducts, excludeIds, search]);

  if (!open) {
    return (
      <Button type="button" variant="outline" size="sm" onClick={() => setOpen(true)} disabled={disabled} className="gap-1.5">
        <Plus className="h-3.5 w-3.5" />
        Lägg till produkt
      </Button>
    );
  }

  return (
    <div className="border border-border rounded-lg p-3 space-y-2 bg-muted/20">
      <Input
        ref={searchRef}
        placeholder="Sök bland dina produkter…"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        className="h-8 text-sm"
      />
      {filtered.length === 0 ? (
        <p className="text-xs text-muted-foreground py-2">
          {allProducts.filter(p => !excludeIds.includes(p.id)).length === 0
            ? 'Inga produkter att lägga till.'
            : 'Inga träffar.'}
        </p>
      ) : (
        <div className="space-y-0.5 max-h-48 overflow-y-auto">
          {filtered.map(p => (
            <button
              key={p.id}
              type="button"
              onClick={() => { onSelect(p); setOpen(false); setSearch(''); }}
              className="w-full text-left px-3 py-2 rounded-md text-sm hover:bg-muted transition-colors"
            >
              {p.title}
            </button>
          ))}
        </div>
      )}
      <Button type="button" variant="ghost" size="sm" onClick={() => { setOpen(false); setSearch(''); }}>Avbryt</Button>
    </div>
  );
}

export function ProductForm({
  mode,
  producerId,
  productId,
  product,
  syncing,
  isSaving,
  onSave,
  onCancel,
  draftSessionId,
  isCreatingDraft = false,
  isPublishBlocked = false,
  dangerZone,
  headerActions,
}: ProductFormProps) {
  const [searchParams, setSearchParams] = useSearchParams();
  const imagesSectionRef = useRef<HTMLDivElement>(null);
  const titleInputRef = useRef<HTMLInputElement>(null);
  const [highlightImages, setHighlightImages] = useState(false);
  const lastAppliedSuggestion = useRef<string>('');
  const interestProductIds = useMemo(() => productId ? [productId] : [], [productId]);
  const { counts: interestCounts } = useProductInterestCounts(interestProductIds);
  const interestCount = productId ? (interestCounts[productId] ?? 0) : 0;

  // Form states
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [priceKr, setPriceKr] = useState('');
  const [productStatus, setProductStatus] = useState<ProductStatusValue>('draft');
  const [productType, setProductType] = useState<ProductTypeValue>('simple');
  const [bundleItems, setBundleItems] = useState<BundleItemData[]>([]);
  const [productImages, setProductImages] = useState<ProductImage[]>([]);
  const [initialized, setInitialized] = useState(mode === 'create');

  // Fetch existing bundle items in edit mode
  const { items: existingBundleItems, loading: bundleItemsLoading } = useBundleItems(
    mode === 'edit' && product?.product_type === 'bundle' ? productId : undefined,
    mode === 'edit' && product?.product_type === 'bundle',
  );

  const initialSnapshot = useRef<{
    title: string; description: string; priceKr: string;
    productStatus: ProductStatusValue; pickupInstructions: string;
    seoTitle: string; seoDescription: string; seoImageUrl: string;
    stockLimit: number | null; productType: ProductTypeValue;
    bundleItemsKey: string;
  } | null>(null);

  /** Stable serialisation of bundle items for dirty-check (ignores display-only title) */
  const bundleItemsKey = useMemo(
    () => JSON.stringify(bundleItems.map(i => ({ p: i.product_id, q: i.quantity, s: i.sort_order }))),
    [bundleItems],
  );

  const [seoTitle, setSeoTitle] = useState('');
  const [seoDescription, setSeoDescription] = useState('');
  const [seoImageUrl, setSeoImageUrl] = useState('');
  const [pickupInstructions, setPickupInstructions] = useState('');
  const [stockLimit, setStockLimit] = useState<number | null>(null);
  const [stockLimitInput, setStockLimitInput] = useState('');

  const [errors, setErrors] = useState<{ title?: string; description?: string }>({});
  const [saveError, setSaveError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Track whether user has manually edited seoTitle
  const [seoTitleTouched, setSeoTitleTouched] = useState(false);

  // Initialize form (edit mode)
  useEffect(() => {
    if (mode === 'edit' && product && !initialized) {
      const t = product.title;
      const d = product.description || '';
      const p = (product.price_ore / 100).toString();
      const st_status = (product.status || 'draft') as ProductStatusValue;
      const pt = (product.product_type || 'simple') as ProductTypeValue;
      const pickup = (product as any).pickup_instructions || '';
      const st = (product as any).seo_title || '';
      const sd = (product as any).seo_description || '';
      const si = (product as any).seo_image_url || '';
      const sl = product.stock_limit ?? null;

      setTitle(t); setDescription(d); setPriceKr(p); setProductStatus(st_status);
      setProductType(pt);
      setProductImages([]);
      setSeoTitle(st); setSeoDescription(sd); setSeoImageUrl(si);
      setPickupInstructions(pickup);
      setStockLimit(sl);
      setStockLimitInput(sl !== null ? String(sl) : '');
      if (st) setSeoTitleTouched(true);

      initialSnapshot.current = {
        title: t, description: d, priceKr: p, productStatus: st_status,
        pickupInstructions: pickup, seoTitle: st, seoDescription: sd, seoImageUrl: si,
        stockLimit: sl, productType: pt, bundleItemsKey: '[]',
      };
      setInitialized(true);
    }
  }, [product, initialized, mode]);

  // Initialize bundle items from fetched data and update snapshot baseline
  useEffect(() => {
    if (existingBundleItems.length > 0 && bundleItems.length === 0 && initialized) {
      const mapped = existingBundleItems.map(item => ({
        product_id: item.product_id,
        quantity: item.quantity,
        sort_order: item.sort_order,
        title: item.title,
      }));
      setBundleItems(mapped);
      // Update snapshot so initial bundle items are not considered dirty
      if (initialSnapshot.current) {
        initialSnapshot.current.bundleItemsKey = JSON.stringify(
          mapped.map(i => ({ p: i.product_id, q: i.quantity, s: i.sort_order })),
        );
      }
    }
  }, [existingBundleItems, initialized]);

  useEffect(() => {
    if (mode === 'create' && !initialSnapshot.current) {
      initialSnapshot.current = {
        title: '', description: '', priceKr: '', productStatus: 'draft',
        pickupInstructions: '', seoTitle: '', seoDescription: '', seoImageUrl: '',
        stockLimit: null, productType: 'simple', bundleItemsKey: '[]',
      };
    }
  }, [mode]);

  const isDirty = useMemo(() => {
    const snap = initialSnapshot.current;
    if (!snap) return false;
    return (
      title !== snap.title || description !== snap.description ||
      priceKr !== snap.priceKr || productStatus !== snap.productStatus ||
      productType !== snap.productType ||
      pickupInstructions !== snap.pickupInstructions ||
      seoTitle !== snap.seoTitle || seoDescription !== snap.seoDescription ||
      seoImageUrl !== snap.seoImageUrl ||
      stockLimit !== snap.stockLimit ||
      bundleItemsKey !== snap.bundleItemsKey
    );
  }, [title, description, priceKr, productStatus, productType, pickupInstructions, seoTitle, seoDescription, seoImageUrl, stockLimit, bundleItemsKey]);

  // Handle focus=images param
  useEffect(() => {
    if (initialized && searchParams.get('focus') === 'images') {
      setTimeout(() => {
        imagesSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        setHighlightImages(true);
        setTimeout(() => setHighlightImages(false), 2000);
        searchParams.delete('focus');
        setSearchParams(searchParams, { replace: true });
      }, 100);
    }
  }, [initialized, searchParams, setSearchParams]);

  const getPlainTextLength = (html: string): number => {
    const temp = document.createElement('div');
    temp.innerHTML = html;
    return (temp.textContent || temp.innerText || '').trim().length;
  };

  const getPlainTextPreview = (html: string, maxLen = 80): string => {
    const temp = document.createElement('div');
    temp.innerHTML = html;
    const plain = (temp.textContent || temp.innerText || '').trim();
    if (!plain) return '';
    return plain.length > maxLen ? plain.slice(0, maxLen - 1) + '…' : plain;
  };

  const validateForPublish = (): boolean => {
    const newErrors: { title?: string; description?: string } = {};
    if (!title.trim()) newErrors.title = 'Titel krävs för att publicera';
    const descriptionLength = getPlainTextLength(description);
    if (descriptionLength < 20) newErrors.description = 'Beskrivningen måste vara minst 20 tecken för att publicera';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleStatusChange = (newStatus: ProductStatusValue) => {
    if ((newStatus === 'public' || newStatus === 'sold_out' || newStatus === 'upcoming') && isPublishBlocked) return;
    if (newStatus === 'public' && !validateForPublish()) return;
    setProductStatus(newStatus);
    setErrors({});
  };

  // Derive effective seoTitle for save – autofill from title when untouched
  const effectiveSeoTitle = seoTitleTouched && seoTitle.trim() ? seoTitle.trim() : '';

  const handleSubmit = useCallback(async () => {
    if (isSubmitting || isSaving) return;
    const status: ProductStatusValue = productStatus;
    if (!title.trim()) {
      setErrors({ title: 'Titel krävs' });
      return;
    }
    if (productStatus === 'public' && !validateForPublish()) return;

    setSaveError(null);
    setIsSubmitting(true);

    try {
      await onSave({
        title: title.trim(), description: description.trim(), priceKr, status,
        productType,
        images: productImages,
        seoTitle: effectiveSeoTitle, seoDescription: seoDescription.trim(),
        seoImageUrl: seoImageUrl.trim(), pickupInstructions: pickupInstructions.trim(),
        stockLimit,
        bundleItems: productType === 'bundle' ? bundleItems : [],
      }, productStatus === 'public');

      initialSnapshot.current = {
        title: title.trim(), description: description.trim(), priceKr, productStatus,
        pickupInstructions: pickupInstructions.trim(),
        seoTitle: effectiveSeoTitle, seoDescription: seoDescription.trim(), seoImageUrl: seoImageUrl.trim(),
        stockLimit, productType, bundleItemsKey,
      };
    } catch (err: any) {
      const msg = err?.message || 'Okänt fel';
      setSaveError(msg);
      console.error('[ProductForm] handleSubmit failed:', err);
    } finally {
      setIsSubmitting(false);
    }
  }, [isSubmitting, isSaving, productStatus, title, description, priceKr, productType, bundleItems, productImages, effectiveSeoTitle, seoDescription, seoImageUrl, pickupInstructions, stockLimit, bundleItemsKey, onSave]);

  const isDisabled = isSaving || isSubmitting || (syncing === productId) || isCreatingDraft;
  const canSave = mode === 'create'
    ? !!title.trim() && !isDisabled
    : isDirty && !!title.trim() && !isDisabled;

  const { imageUrl: previewImageUrl } = useProductPreviewImage(productId);

  const errorsCount = Object.keys(errors).filter(k => !!(errors as any)[k]).length;

  /* ── Quick-start handler (simple products) ── */
  const handleQuickStart = (chip: typeof QUICK_START_CHIPS[number]) => {
    if (chip.title) {
      setTitle(chip.title);
      if (errors.title) setErrors({ ...errors, title: undefined });
    }
    // Focus title input for immediate editing
    setTimeout(() => titleInputRef.current?.focus(), 50);
  };

  /* ── Section summaries ── */
  const grundSummary = (() => {
    const parts: string[] = [];
    if (title.trim()) parts.push(title.trim().length > 30 ? title.trim().slice(0, 27) + '…' : title.trim());
    if (priceKr && parseFloat(priceKr) > 0) parts.push(`${priceKr} kr`);
    return parts.length > 0 ? parts.join(' · ') : 'Fyll i titel och pris';
  })();

  const descSummary = (() => {
    const preview = getPlainTextPreview(description);
    return preview || 'Lägg till en beskrivning';
  })();

  const imagesSummary = (() => {
    const count = productImages.length;
    if (count === 0) return mode === 'create' ? 'Lägg till bilder' : 'Inga bilder';
    return `${count} bild${count > 1 ? 'er' : ''}`;
  })();

  const pickupSummary = pickupInstructions.trim() ? 'Egen instruktion' : 'Upphämtning';
  const availabilitySummary = stockLimit !== null ? `Max ${stockLimit} st` : 'Lager';
  const seoSummary = seoTitle.trim() ? seoTitle.trim() : 'Google och delning';

  /* ── Save-disabled hint ── */
  const saveDisabledHint = (() => {
    if (isDisabled) return 'Sparar…';
    if (!title.trim()) return 'Fyll i en titel för att spara';
    if (mode === 'edit' && !isDirty) return 'Inga ändringar att spara';
    return null;
  })();

  /* ── Accordion open state ── */
  const [openSections, setOpenSections] = useState<string[]>(
    mode === 'create' ? ['grund'] : ['grund']
  );

  // Auto-open bundle-items when switching to bundle
  useEffect(() => {
    if (productType === 'bundle' && !openSections.includes('bundle-items')) {
      setOpenSections(prev => [...prev, 'bundle-items']);
    }
    if (productType === 'simple') {
      setOpenSections(prev => prev.filter(s => s !== 'bundle-items'));
    }
  }, [productType]);

  /* ── Accordion sections ── */
  const accordionSections = (
    <EditorSectionCard>
      {/* 1. Titel och pris */}
      <EditorSectionItem title="Grund" icon={Package} defaultOpen={mode === 'create' || openSections.includes('grund')} hint={grundSummary}>
        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="product-title">Titel *</Label>
            <Input
              id="product-title"
              ref={titleInputRef}
              placeholder="T.ex. Nötkött från gården"
              value={title}
              onChange={(e) => {
                setTitle(e.target.value);
                if (errors.title) setErrors({ ...errors, title: undefined });
                if (saveError) setSaveError(null);
              }}
              disabled={isDisabled}
              className={cn(errors.title && 'border-destructive')}
            />
            {errors.title && <p className="text-sm text-destructive">{errors.title}</p>}
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="product-price">Pris (kr)</Label>
            <Input
              id="product-price"
              type="number"
              min="0"
              step="1"
              placeholder="0"
              value={priceKr}
              onChange={(e) => setPriceKr(e.target.value)}
              disabled={isDisabled}
            />
          </div>

          {/* Product type toggle */}
          <div className="space-y-1.5 pt-2 border-t border-border/50">
            <Label>Produkttyp</Label>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => setProductType('simple')}
                disabled={isDisabled}
                className={cn(
                  'inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-sm border transition-colors',
                  productType === 'simple'
                    ? 'border-primary bg-primary/10 text-primary'
                    : 'border-border text-muted-foreground hover:bg-muted',
                )}
              >
                <Package className="h-3.5 w-3.5" />
                Enkel produkt
              </button>
              <button
                type="button"
                onClick={() => setProductType('bundle')}
                disabled={isDisabled}
                className={cn(
                  'inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-sm border transition-colors',
                  productType === 'bundle'
                    ? 'border-primary bg-primary/10 text-primary'
                    : 'border-border text-muted-foreground hover:bg-muted',
                )}
              >
                <PackageOpen className="h-3.5 w-3.5" />
                Paket
              </button>
            </div>
            {productType === 'bundle' && (
              <div className="space-y-2">
                <p className="text-xs text-muted-foreground">
                  Ett paket har eget pris och lager. Innehållet visas för kunden men påverkar inte enskilda produkters lagersaldo.
                </p>
                <div className="space-y-1.5">
                  <p className="text-xs font-medium text-muted-foreground">Vanliga paket</p>
                  <div className="flex flex-wrap gap-1.5">
                    {[
                      'Gårdslåda', 'Köttlåda', 'Grillåda', 'Grönsakskasse',
                      'Picknickkorg', 'Presentkorg', 'Prova-på-paket',
                    ].map(suggestion => (
                      <button
                        key={suggestion}
                        type="button"
                        disabled={isDisabled}
                        onClick={() => {
                          if (!title.trim() || title === lastAppliedSuggestion.current) {
                            setTitle(suggestion);
                            lastAppliedSuggestion.current = suggestion;
                            if (errors.title) setErrors({ ...errors, title: undefined });
                          }
                          setTimeout(() => titleInputRef.current?.focus(), 50);
                        }}
                        className="inline-flex items-center rounded-full border border-border bg-muted/40 px-2.5 py-1 text-xs text-foreground hover:bg-muted transition-colors"
                      >
                        {suggestion}
                      </button>
                    ))}
                    <button
                      type="button"
                      disabled={isDisabled}
                      onClick={() => {
                        setTimeout(() => titleInputRef.current?.focus(), 50);
                      }}
                      className="inline-flex items-center rounded-full border border-dashed border-border px-2.5 py-1 text-xs text-muted-foreground hover:bg-muted transition-colors"
                    >
                      Annat paket
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </EditorSectionItem>

      {/* 1b. Paketinnehåll */}
      {productType === 'bundle' && (
        <EditorSectionItem title="Innehåll" icon={PackageOpen} defaultOpen hint={bundleItems.length > 0 ? `${bundleItems.length} produkt${bundleItems.length > 1 ? 'er' : ''} i paketet` : 'Innehåll'}>
          <div className="space-y-3">
            <p className="text-xs text-muted-foreground">
              Lägg till produkter som ingår i paketet. Paketet har eget pris och eget lager.
            </p>
            <p className="text-xs text-muted-foreground">
              Innehållet visas för kunden men påverkar inte enskilda produkters lagersaldo.
            </p>

            {bundleItemsLoading ? (
              <div className="flex items-center gap-2 py-4 justify-center">
                <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                <span className="text-sm text-muted-foreground">Laddar innehåll…</span>
              </div>
            ) : (
              <>
                {bundleItems.length === 0 && (
                  <div className="rounded-lg border border-dashed border-border bg-muted/20 p-6 text-center space-y-2">
                    <PackageOpen className="h-8 w-8 text-muted-foreground mx-auto" />
                    <p className="text-sm font-medium text-foreground">Det här paketet innehåller ännu inga produkter.</p>
                    <p className="text-xs text-muted-foreground">Lägg till de produkter som ska ingå i paketet.</p>
                  </div>
                )}

                {bundleItems.length > 0 && (
                  <div className="space-y-2">
                    {bundleItems.map((item, idx) => (
                      <div key={item.product_id} className="flex items-center gap-3 p-2 rounded-md border border-border bg-muted/30">
                        <div className="flex-1 min-w-0">
                          <span className="text-sm font-medium text-foreground truncate block">
                            {item.title || 'Okänd produkt'}
                          </span>
                        </div>
                        <div className="flex items-center gap-1.5 shrink-0">
                          <button
                            type="button"
                            onClick={() => {
                              const updated = [...bundleItems];
                              updated[idx] = { ...updated[idx], quantity: Math.max(1, updated[idx].quantity - 1) };
                              setBundleItems(updated);
                            }}
                            className="h-7 w-7 rounded border border-border flex items-center justify-center hover:bg-muted"
                            disabled={item.quantity <= 1}
                          >
                            <Minus className="h-3 w-3" />
                          </button>
                          <span className="text-sm font-medium w-6 text-center">{item.quantity}</span>
                          <button
                            type="button"
                            onClick={() => {
                              const updated = [...bundleItems];
                              updated[idx] = { ...updated[idx], quantity: updated[idx].quantity + 1 };
                              setBundleItems(updated);
                            }}
                            className="h-7 w-7 rounded border border-border flex items-center justify-center hover:bg-muted"
                          >
                            <Plus className="h-3 w-3" />
                          </button>
                          <button
                            type="button"
                            onClick={() => setBundleItems(bundleItems.filter((_, i) => i !== idx))}
                            className="h-7 w-7 rounded border border-destructive/30 flex items-center justify-center hover:bg-destructive/10 ml-1"
                          >
                            <Trash2 className="h-3 w-3 text-destructive" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                <BundleProductPicker
                  producerId={producerId}
                  excludeIds={[...(productId ? [productId] : []), ...bundleItems.map(i => i.product_id)]}
                  onSelect={(product) => {
                    setBundleItems([...bundleItems, {
                      product_id: product.id,
                      quantity: 1,
                      sort_order: bundleItems.length,
                      title: product.title,
                    }]);
                  }}
                  disabled={isDisabled}
                />
              </>
            )}
          </div>
        </EditorSectionItem>
      )}

      {/* 2. Beskrivning */}
      <EditorSectionItem title="Beskrivning" icon={FileText} hint={descSummary}>
        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="product-description">Beskrivning</Label>
            <RichTextEditor
              value={description}
              onChange={(html) => {
                setDescription(html);
                if (errors.description) setErrors({ ...errors, description: undefined });
              }}
              disabled={isDisabled}
              placeholder="Beskriv produkten, dess innehåll och egenskaper..."
            />
            {errors.description && <p className="text-sm text-destructive">{errors.description}</p>}
          </div>
        </div>
      </EditorSectionItem>

      {/* 3. Bilder */}
      {(productId || draftSessionId) && (
        <EditorSectionItem title="Bilder" icon={Image} hint={imagesSummary}>
          <div
            ref={imagesSectionRef}
            className={cn(
              'transition-all duration-500 rounded-lg',
              highlightImages && 'ring-2 ring-primary ring-offset-2 ring-offset-background',
            )}
          >
            <ProductImagesSection
              producerId={producerId}
              productId={productId}
              draftSessionId={draftSessionId}
            />
          </div>
        </EditorSectionItem>
      )}

      <EditorSectionItem title="Upphämtning" icon={Truck} hint={pickupSummary}>
        <div className="space-y-1.5">
          <Label htmlFor="pickup-instructions">
            Särskilda upphämtningsinstruktioner <span className="text-muted-foreground text-xs">(valfritt)</span>
          </Label>
          <p className="text-xs text-muted-foreground" id="pickup-instructions-desc">
            Om du fyller i detta ersätter det gårdens generella utlämningsinformation för just den här produkten.
          </p>
          <Textarea
            id="pickup-instructions"
            aria-describedby="pickup-instructions-desc"
            placeholder={"T.ex. Hämtas kyld, ta med kylväska.\nRing 070-xxx vid ankomst."}
            value={pickupInstructions}
            onChange={(e) => setPickupInstructions(e.target.value)}
            disabled={isDisabled}
            rows={2}
          />
        </div>
      </EditorSectionItem>

      {/* 5. Antal tillgängliga */}
      <EditorSectionItem title="Lager" icon={BoxSelect} hint={availabilitySummary}>
        <div className="space-y-1.5">
          <Label htmlFor="stock-limit">
            Antal tillgängliga <span className="text-muted-foreground text-xs">(valfritt)</span>
          </Label>
          <p className="text-xs text-muted-foreground" id="stock-limit-desc">
            Lämna tomt om antalet inte är begränsat. Om du anger ett antal förhindras beställningar som överskrider lagret.
          </p>
          <Input
            id="stock-limit"
            aria-describedby="stock-limit-desc"
            type="number"
            min="0"
            step="1"
            placeholder="Obegränsat"
            value={stockLimitInput}
            onChange={(e) => {
              const raw = e.target.value;
              setStockLimitInput(raw);
              if (raw === '') {
                setStockLimit(null);
              } else {
                const n = parseInt(raw, 10);
                setStockLimit(Number.isFinite(n) && n >= 0 ? n : null);
              }
            }}
            disabled={isDisabled}
          />
        </div>
      </EditorSectionItem>

      {/* 6. Google och delning */}
      <EditorSectionItem title="Google och delning" icon={Globe} hint={seoSummary}>
        <div className="space-y-4">
          <p className="text-xs text-muted-foreground">
            Valfritt. Vi fyller i detta automatiskt om du lämnar det tomt.
          </p>

          <div className="space-y-1.5">
            <div className="flex items-center justify-between">
              <Label htmlFor="seo-title" className="text-sm">Titel i Google</Label>
              <span className={cn('text-xs', seoTitle.length > 60 ? 'text-destructive' : 'text-muted-foreground')}>
                {seoTitle.length}/60
              </span>
            </div>
            <Input
              id="seo-title"
              placeholder={title.trim() || 'Fylls i från produkttiteln'}
              value={seoTitle}
              onChange={(e) => {
                setSeoTitle(e.target.value.slice(0, 60));
                setSeoTitleTouched(true);
              }}
              disabled={isDisabled}
              maxLength={60}
            />
          </div>

          <div className="space-y-1.5">
            <div className="flex items-center justify-between">
              <Label htmlFor="seo-description" className="text-sm">Beskrivning i Google</Label>
              <span className={cn('text-xs', seoDescription.length > 160 ? 'text-destructive' : 'text-muted-foreground')}>
                {seoDescription.length}/160
              </span>
            </div>
            <Textarea
              id="seo-description"
              placeholder="Kort text som visas i Google och vid delning…"
              value={seoDescription}
              onChange={(e) => setSeoDescription(e.target.value.slice(0, 160))}
              disabled={isDisabled}
              rows={2}
              maxLength={160}
            />
          </div>

          {/* SERP Preview */}
          <div className="border-t pt-3 mt-1">
            <p className="text-xs font-medium text-muted-foreground mb-2">Förhandsvisning</p>
            <div className="bg-background border rounded-lg p-3 space-y-0.5">
              <p className="text-sm text-primary hover:underline cursor-default truncate">
                {seoTitle.trim() || title.trim() || 'Produkttitel'}
              </p>
              <p className="text-xs text-green-700 dark:text-green-500 truncate">
                farmly.se/produkter/din-gard/{title ? title.toLowerCase().replace(/[^a-z0-9åäö]+/gi, '-').replace(/^-|-$/g, '') : 'produkt'}
              </p>
              <p className="text-xs text-muted-foreground line-clamp-2">
                {seoDescription.trim() || (() => {
                  const temp = document.createElement('div');
                  temp.innerHTML = description;
                  const plain = (temp.textContent || temp.innerText || '').trim();
                  return plain.length > 160 ? plain.slice(0, 157) + '...' : plain;
                })() || 'Beskrivning av produkten visas här...'}
              </p>
            </div>
          </div>
        </div>
      </EditorSectionItem>

      {/* 7. Farozon (edit mode only) */}
      {mode === 'edit' && dangerZone && (
        <EditorSectionItem title="Farozon" icon={AlertTriangle} hint="Farozon">
          {dangerZone}
        </EditorSectionItem>
      )}
    </EditorSectionCard>
  );

  /* ── Mobile status control (inline above accordion on mobile) ── */
  const mobileStatusControl = (
    <div className="space-y-2">
      <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Status</p>
      {isPublishBlocked && (
        <div className="flex items-start gap-2 p-2 rounded-md border border-amber-200 bg-amber-50 dark:border-amber-900 dark:bg-amber-950/30">
          <span className="text-amber-600 dark:text-amber-400 text-xs">⚠️</span>
          <p className="text-xs text-amber-700 dark:text-amber-300">
            Publicering kräver godkänt konto.
          </p>
        </div>
      )}
      <select
        value={productStatus}
        onChange={(e) => handleStatusChange(e.target.value as ProductStatusValue)}
        disabled={isDisabled}
        className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
      >
        {STATUS_OPTIONS.map(opt => (
          <option key={opt.value} value={opt.value}>{opt.label}</option>
        ))}
      </select>
      <p className="text-xs text-muted-foreground">{getStatusHint(productStatus)}</p>
    </div>
  );

  /* ── Mobile sticky footer (canonical StickyBottomBar pattern) ── */
  const stickyFooter = (
    <StickyBottomBar>
      <div className="w-full flex items-center justify-between gap-2">
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={onCancel}
          disabled={isDisabled}
          className="h-9 text-xs"
        >
          <ArrowLeft className="h-3.5 w-3.5 mr-1.5" />
          {mode === 'create' ? 'Avbryt' : 'Tillbaka'}
        </Button>
        <div className="flex items-center gap-2">
          {/* Compact status select on mobile */}
          <select
            value={productStatus}
            onChange={(e) => handleStatusChange(e.target.value as ProductStatusValue)}
            disabled={isDisabled}
            className="h-9 rounded-md border border-input bg-transparent px-2 py-1 text-xs shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
          >
            {STATUS_OPTIONS.map(opt => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
          <SaveButton
            type="button"
            size="sm"
            onClick={handleSubmit}
            disabled={!canSave}
            isSaving={isSaving || isSubmitting || isCreatingDraft}
            savingLabel="Sparar…"
            className="h-9 text-xs"
          >
            <Save className="h-3.5 w-3.5 mr-1.5" />
            Spara
          </SaveButton>
        </div>
      </div>
    </StickyBottomBar>
  );

  /* ── Quick-start section (create mode, simple products only) ── */
  const quickStartSection = mode === 'create' && !title.trim() && productType === 'simple' ? (
    <div className="space-y-2">
      <div className="flex items-center gap-2">
        <Sparkles className="h-4 w-4 text-muted-foreground" />
        <p className="text-sm font-medium text-foreground">Snabbstart</p>
      </div>
      <p className="text-xs text-muted-foreground">Välj en produkttyp för att komma igång snabbt.</p>
      <div className="flex flex-wrap gap-2">
        {QUICK_START_CHIPS.map(chip => (
          <button
            key={chip.label}
            type="button"
            onClick={() => handleQuickStart(chip)}
            className="inline-flex items-center rounded-full border border-border bg-muted/40 px-3 py-1.5 text-sm text-foreground hover:bg-muted transition-colors"
          >
            {chip.label}
          </button>
        ))}
      </div>
    </div>
  ) : null;

  /* ── Preview column (left / main) ── */
  const previewColumn = (
    <div className="space-y-5">
      {/* Inline save error banner */}
      {saveError && (
        <div className="flex items-start gap-2 p-3 rounded-lg border border-destructive/50 bg-destructive/5">
          <AlertCircle className="h-4 w-4 text-destructive mt-0.5 shrink-0" />
          <div className="min-w-0">
            <p className="text-sm font-medium text-destructive">Det gick inte att spara</p>
            <p className="text-xs text-muted-foreground mt-0.5">Försök igen. Kontakta support om problemet kvarstår.</p>
            {import.meta.env.DEV && (
              <p className="text-xs text-destructive/70 mt-1 break-all">{saveError}</p>
            )}
          </div>
        </div>
      )}

      {/* Product preview card — visitor-facing */}
      <div className="rounded-xl overflow-hidden border border-border/40 bg-card">
        {previewImageUrl ? (
          <img src={previewImageUrl} alt={title || 'Produkt'} className="w-full aspect-[4/3] object-cover" />
        ) : (
          <div className="w-full aspect-[4/3] bg-muted flex flex-col items-center justify-center gap-2">
            <Image className="h-10 w-10 text-muted-foreground/20" />
            <p className="text-xs text-muted-foreground">Ingen omslagsbild ännu</p>
          </div>
        )}
        <div className="p-5 space-y-3">
          <h2 className="text-xl font-semibold text-foreground leading-tight">
            {title.trim() || 'Produkttitel'}
          </h2>
          {priceKr && parseFloat(priceKr) > 0 && (
            <p className="text-lg font-semibold text-foreground">{priceKr} kr</p>
          )}
          {description && (
            <div
              className="text-sm text-muted-foreground line-clamp-4 prose prose-sm max-w-none [&>*:first-child]:mt-0"
              dangerouslySetInnerHTML={{ __html: description }}
            />
          )}
          <div className="flex flex-wrap items-center gap-3 pt-1">
            {productType === 'bundle' && (
              <span className="inline-flex items-center gap-1 text-xs text-muted-foreground">
                <PackageOpen className="h-3.5 w-3.5" /> Paket
              </span>
            )}
            {stockLimit !== null && (
              <span className="text-xs text-muted-foreground">Max {stockLimit} st</span>
            )}
          </div>
        </div>
      </div>

      {/* Interest info */}
      {mode === 'edit' && interestCount > 0 && (
        <div className="flex items-center gap-2 text-sm text-muted-foreground px-1">
          <Users className="h-4 w-4 shrink-0" />
          <span>{interestCount} {interestCount === 1 ? 'person har' : 'personer har'} visat intresse</span>
        </div>
      )}

      {/* DEV debug strip */}
      {import.meta.env.DEV && (
        <div className="text-[10px] font-mono bg-muted/50 border border-border rounded p-2 space-y-1">
          <div className="flex flex-wrap gap-x-3 gap-y-0.5">
            <span>dirty={String(isDirty)}</span>
            <span>canSave={String(canSave)}</span>
            <span>isSubmitting={String(isSubmitting)}</span>
            <span>isSaving={String(isSaving)}</span>
            <span>errors={errorsCount}</span>
            <span>mode={mode}</span>
            <span>prodId={productId ?? '—'}</span>
          </div>
          {saveError && <div className="text-destructive truncate">lastErr: {saveError}</div>}
          <Button type="button" variant="outline" size="sm" className="h-6 text-[10px] mt-1" onClick={handleSubmit}>
            Force submit (DEV)
          </Button>
        </div>
      )}
    </div>
  );

  /* ── Controls sidebar (right) ── */
  const controlsSidebar = (
    <div className="space-y-5">
      {/* Settings – status & visibility */}
      <EditorSidebarSection heading="Inställningar">
        <EditorSidebarCard>
          {isPublishBlocked && (
            <div className="flex items-start gap-2 p-2 rounded-md border border-amber-200 bg-amber-50 dark:border-amber-900 dark:bg-amber-950/30">
              <span className="text-amber-600 dark:text-amber-400 text-xs">⚠️</span>
              <p className="text-xs text-amber-700 dark:text-amber-300">
                Publicering kräver godkänt konto.
              </p>
            </div>
          )}
          <div className="space-y-1.5">
            <label className="text-sm text-muted-foreground">Status</label>
            <select
              value={productStatus}
              onChange={(e) => handleStatusChange(e.target.value as ProductStatusValue)}
              disabled={isDisabled}
              className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
            >
              {STATUS_OPTIONS.map(opt => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
            <p className="text-xs text-muted-foreground">{getStatusHint(productStatus)}</p>
          </div>
        </EditorSidebarCard>
      </EditorSidebarSection>

      {/* Content editing sections */}
      {quickStartSection}
      {accordionSections}

      {/* Actions */}
      <EditorSidebarSection>
        <div className="space-y-2">
          <Button onClick={handleSubmit} disabled={!canSave} className="w-full">
            {(isSaving || isSubmitting || isCreatingDraft) && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
            {mode === 'create' ? 'Spara produkt' : 'Spara ändringar'}
          </Button>
          <Button variant="outline" onClick={onCancel} disabled={isDisabled} className="w-full">
            {mode === 'create' ? 'Avbryt' : 'Tillbaka'}
          </Button>
          {saveDisabledHint && !canSave && (
            <p className="text-xs text-muted-foreground text-center">{saveDisabledHint}</p>
          )}
        </div>
      </EditorSidebarSection>
    </div>
  );

  /* ── Page header ── */
  const pageHeader = (
    <div className="flex items-center justify-between gap-3">
      <div>
        <h1 className="text-2xl font-semibold text-foreground">
          {mode === 'create' ? 'Skapa produkt' : 'Redigera produkt'}
        </h1>
        <p className="text-muted-foreground mt-1">
          {mode === 'create'
            ? 'Fyll i produktinformationen nedan och klicka spara.'
            : 'Uppdatera produktinformationen nedan.'}
        </p>
      </div>
      {headerActions}
    </div>
  );

  return (
    <>
      <EditorLayout header={pageHeader} main={previewColumn} sidebar={controlsSidebar} />
      {stickyFooter}
    </>
  );
}
